import { EDSComponent, AttributeUtils } from "@emartech/eds-utils-component";
import { render, html } from "uhtml";
import classNames from "clsx";
import {
  add,
  remove,
  moveUp,
  moveDown,
  moveBottom,
  moveTop,
  moveTo,
  isLast,
  isFirst,
  update,
} from "../../services/order.service";
import { TranslationService } from "../../services/translation.service";
import { OrderItemInterface } from "../order-item/order-item.component";

const CONTAINER_CLASS_NAME = "e-order";

export class OrderComponent extends EDSComponent {
  static componentName = "order";

  _items: any[] = [];
  _columns: number;
  _editing: boolean = false;

  _container: HTMLElement;

  private _draggedItem: any;
  private _selectedItem: any;
  private _keepSelectedState: boolean;
  private _droplineItem: any;
  private _translationService: any;

  init() {
    this.createRenderRoot();

    this._translationService = TranslationService.create();

    this.addEventListener("order-item", (event: CustomEvent<OrderItemInterface>) => {
      this._addItem(event.detail);

      event.target.addEventListener("remove-item", (removeEvent: CustomEvent<OrderItemInterface>) =>
        this._removeItem(removeEvent.detail),
      );
      event.target.addEventListener("update-item", (updateEvent: CustomEvent<OrderItemInterface>) =>
        this._updateItem(updateEvent.detail),
      );
    });
  }

  static get observedAttributes() {
    return ["columns", "editing"];
  }

  set columns(value) {
    this._columns = value;
    this.requestRender();
  }

  set editing(value) {
    this._editing = AttributeUtils.convertToBoolean(value);
    this.reflectStringAttribute("editing", this._editing.toString());
    this.requestRender();
  }

  connectedCallback() {
    if (!this.querySelector(CONTAINER_CLASS_NAME)) {
      this.appendChild(this._container);
    }

    this._translationService.fetch().then(() => {
      this.requestRender();
    });
  }

  render() {
    if (!this._container) {
      return;
    }

    render(
      this._container,
      html`
        ${this._items.map(
          (item) => html`
            <div
              class="${classNames("e-order__item_container e-padding-s", {
                "e-order__item_container-selected": this._editing && item === this._selectedItem,
                "e-order__item_container-dropline": this._editing && item === this._droplineItem,
              })}"
              style="${`order: ${item.order}; width: ${item.width}%`}"
            >
              <div
                draggable="${this._editing}"
                class="e-order__item"
                @dragstart="${this._handleDragStart.bind(this, item)}"
                @dragenter="${this._handleDroplines.bind(this, item)}"
                @dragover="${this._allowDrop}"
                @drop="${this._handleDrop.bind(this, item)}"
                @mouseenter="${this._handleSelection.bind(this, item)}"
                @click="${this._handleSelection.bind(this, item)}"
              >
                <div class="e-order__item_content" ref="${(node) => (item.container = node)}"></div>
              </div>
              <div class="e-order__item_toolbar">
                <div class="e-inputgroup">
                  <e-dropdown
                    data-markup-class="e-btn e-btn-dropdown e-btn-borderless e-inputgroup__item e-inputgroup__item-first"
                    @status="${this._handleDropdownToggle.bind(this)}"
                  >
                    <e-dropdown-handler>
                      <e-icon icon="e-move"></e-icon>
                      ${this._translationService.get("itemToolbar.move")}
                    </e-dropdown-handler>
                    <div class="e-dropdown__content">
                      <a
                        class="${classNames("e-dropdown__item js-move-top", {
                          "e-dropdown__item-disabled": isFirst(this._items, item),
                        })}"
                        @click="${this._handleTopClick.bind(this, item)}"
                      >
                        <e-icon icon="e-move-top" type="table"></e-icon>
                        ${this._translationService.get("itemToolbar.top")}
                      </a>
                      <a
                        class="${classNames("e-dropdown__item js-move-up", {
                          "e-dropdown__item-disabled": isFirst(this._items, item),
                        })}"
                        @click="${this._handleUpClick.bind(this, item)}"
                      >
                        <e-icon icon="e-move-up" type="table"></e-icon>
                        ${this._translationService.get("itemToolbar.up")}
                      </a>
                      <a
                        class="${classNames("e-dropdown__item js-move-down", {
                          "e-dropdown__item-disabled": isLast(this._items, item),
                        })}"
                        @click="${this._handleDownClick.bind(this, item)}"
                      >
                        <e-icon icon="e-move-down" type="table"></e-icon>
                        ${this._translationService.get("itemToolbar.down")}
                      </a>
                      <a
                        class="${classNames("e-dropdown__item js-move-bottom", {
                          "e-dropdown__item-disabled": isLast(this._items, item),
                        })}"
                        @click="${this._handleBottomClick.bind(this, item)}"
                      >
                        <e-icon icon="e-move-bottom" type="table"></e-icon>
                        ${this._translationService.get("itemToolbar.bottom")}
                      </a>
                    </div>
                  </e-dropdown>
                  <button
                    class="e-btn e-btn-borderless e-inputgroup__item e-inputgroup__item-last js-remove"
                    @click="${this._removeItem.bind(this, item)}"
                  >
                    <e-icon icon="close"></e-icon>
                    ${this._translationService.get("itemToolbar.remove")}
                  </button>
                </div>
              </div>
            </div>
          `,
        )}
      `,
    );

    this._items.forEach((item) => {
      if (!item.content || item.content.parentNode === item.container) {
        return;
      }

      item.container.innerHTML = "";
      item.container.appendChild(item.content);
    });
  }

  createRenderRoot() {
    if (!this._container) {
      this._container = document.createElement("div");
      this._container.className = CONTAINER_CLASS_NAME;
    }

    return this._container;
  }

  _handleDropdownToggle(event) {
    if (event.detail.status === "opened") {
      this._keepSelectedState = true;
    } else if (event.detail.status === "closed") {
      this._keepSelectedState = false;
    }
  }

  _handleSelection(item: OrderItemInterface) {
    if (this._keepSelectedState || !this._editing) {
      return;
    }

    this._selectedItem = this._items.find((currentItem) => currentItem === item);
    this._items = [...this._items];
    this.requestRender();
  }

  _handleTopClick(item: OrderItemInterface) {
    this._items = moveTop(this._items, item);
    this.requestRender();
    this._dispatchChangeEvent();
  }

  _handleUpClick(item: OrderItemInterface) {
    this._items = moveUp(this._items, item);
    this.requestRender();
    this._dispatchChangeEvent();
  }

  _handleDownClick(item: OrderItemInterface) {
    this._items = moveDown(this._items, item);
    this.requestRender();
    this._dispatchChangeEvent();
  }

  _handleBottomClick(item: OrderItemInterface) {
    this._items = moveBottom(this._items, item);
    this.requestRender();
    this._dispatchChangeEvent();
  }

  _handleDragStart(item: OrderItemInterface) {
    if (!this._editing) {
      return;
    }

    this._keepSelectedState = true;
    this._draggedItem = item;
    this.requestRender();
  }

  _handleDroplines(item) {
    if (!this._draggedItem) {
      return;
    }

    this._droplineItem = this._items.find((currentItem) => currentItem === item);
    this._items = [...this._items];
    this.requestRender();
  }

  _handleDrop(item) {
    if (!this._draggedItem) {
      return;
    }

    this._items = moveTo(this._items, this._draggedItem, item);
    this._draggedItem = null;
    this._droplineItem = null;
    this._dispatchChangeEvent();

    this.requestRender().then(() => (this._keepSelectedState = false));
  }

  _allowDrop(event) {
    event.preventDefault();
  }

  _removeItem(item: OrderItemInterface) {
    this._items = remove(this._items, item);
    this.requestRender();
    this._dispatchChangeEvent();
  }

  _updateItem(item: OrderItemInterface) {
    this._items = update(this._items, item, this._columns);
    this.requestRender();
  }

  _addItem(item: OrderItemInterface) {
    this._items = add(this._items, item, this._columns);
    this.requestRender();
  }

  _dispatchChangeEvent() {
    const changeEvent = new CustomEvent("change", {
      detail: {
        items: this._items,
      },
    });

    this.dispatchEvent(changeEvent);
  }
}
